import {I18n} from 'aws-amplify/utils';
import {
    Drawer,
    DrawerProps,
    ScrollArea,
} from '@mantine/core';

import {RevalidateForm} from '../forms/RevalidateForm';
import {RevalidateFormValues} from '../forms/types';
import {useRotateProviderCredentialMutation} from '../hooks/useProviderConnectionsQuery';
import {
    buildRotateInput,
    ConnectProviderCredential,
    ProviderConnection,
} from '../types';
import {
    showErrorNotifications,
    showSuccessNotification,
} from '../../../components/ServiceNotifications';

// FNP-1248 — THE RE-VALIDATE DRAWER. Step 3 of the three-step flow.
//
// ⚠️ THIS CLOSES ON SUCCESS, UNLIKE THE CONNECT DRAWER.
//
// The connect drawer stays open because it has something to hand over: a
// freshly-minted external id the tenant has not seen. A rotation mints nothing
// — the connection KEEPS its external id, which is what makes the trust policy
// the tenant already wrote stay correct. There is no new value to deliver, so
// the drawer closes and the detail page behind it shows the updated status.
//
// ⚠️ A ROTATION THAT LANDS INVALID IS STILL A SUCCESSFUL CALL. The connection is
// kept, the failure is recorded, and the tenant is told plainly. It is never
// deleted for being broken, and this drawer never treats a non-ACTIVE result as
// a rejected submit.

export const RevalidateDrawer = (
    {
        connection,
        roleArn,
        opened,
        onClose,
        ...rest
    }: DrawerProps & {
        connection: ProviderConnection,
        roleArn?: string,
    },
) => {

    const rotateMutation = useRotateProviderCredentialMutation();

    const handleSubmit = async (values: RevalidateFormValues) => {

        // Exactly ONE field, chosen by the connection's own credential type —
        // never by which input is non-empty. A rotation cannot change the
        // credential SHAPE any more than it can change the provider.
        const credential: ConnectProviderCredential =
            connection.credentialType === 'ASSUMED_ROLE'
                ? {roleArn: values.roleArn.trim()}
                : {apiToken: values.apiToken};

        try {
            const updated = await rotateMutation.mutateAsync(
                buildRotateInput(connection.id, credential, values.writable),
            );

            // ⚠️ THE MESSAGE REPORTS THE OUTCOME, NOT THE ACTION. "Credential
            // updated" next to a still-INVALID badge reads as the system
            // contradicting itself; the tenant needs to know whether the thing
            // they came to fix is actually fixed.
            if (updated?.status === 'ACTIVE') {
                showSuccessNotification(I18n.get('Connection validated. Flexi can now reach this provider.'));
            } else if (updated?.status === 'UNREACHABLE') {
                showSuccessNotification(I18n.get('Credential saved, but the provider could not be reached to check it. This is worth retrying shortly.'));
            } else {
                showSuccessNotification(I18n.get('Credential saved, but the provider still rejected it. Check that the external ID is in your role\'s trust policy exactly as shown.'));
            }

            onClose();

        } catch (e) {
            // The CALL failed. The drawer stays open with the tenant's input.
            showErrorNotifications(e);
            throw e;
        }
    };

    return (
        <Drawer
            transitionProps={{
                transition: 'slide-up',
                duration: 750,
                timingFunction: 'ease',
            }}
            position={'right'}
            offset={10}
            opened={opened}
            onClose={onClose}
            title={I18n.get('Re-validate connection')}
            padding={'xl'}
            size={'xl'}
            scrollAreaComponent={ScrollArea.Autosize}
            {...rest}
        >
            <RevalidateForm
                connection={connection}
                roleArn={roleArn}
                handleSubmit={handleSubmit}
                handleCancel={onClose}
            />
        </Drawer>
    );
};
